// Deprecated alias. The main-window label's canonical home is the
// dependency-free `@/tauri/window-labels` (`MAIN_WINDOW_LABEL`); this re-export
// keeps existing RPA / dictation / overlay call sites working under the old name
// while preserving a single source of truth. Re-exported from `window-labels`
// (not `@/tauri/window`) so this module stays a leaf and importing it never
// pulls in the Tauri window API. New code should import `MAIN_WINDOW_LABEL`.
export { MAIN_WINDOW_LABEL as RPA_MAIN_WINDOW_LABEL } from './window-labels';

export type RpaAuxiliaryWindowId =
  | 'overlay'
  | 'recording-toolbar'
  | 'cua-status'
  | 'cua-border'
  | 'rpa-flow-element-highlight';

export type RpaAuxiliaryWindowSpec = {
  id: RpaAuxiliaryWindowId;
  label: RpaAuxiliaryWindowId;
  route: `/${string}`;
  // Optional: only present where the size is a real runtime source of truth and
  // pinned by a test (the recording-toolbar). The overlay is built in
  // `tauri-plugin-rpa` from its own `OVERLAY_DEFAULT_*` constants, so carrying a
  // size here would read as authoritative while being unasserted and prone to
  // drift — it is intentionally omitted.
  size?: {
    width: number;
    height: number;
  };
  bootstrapMode: 'rpa-auxiliary-window';
};

export const RPA_AUXILIARY_WINDOWS = {
  overlay: {
    id: 'overlay',
    label: 'overlay',
    route: '/overlay',
    bootstrapMode: 'rpa-auxiliary-window',
  },
  cuaStatus: {
    id: 'cua-status',
    label: 'cua-status',
    route: '/cua-status',
    // Geometry is owned by tauri-plugin-rpa (`STATUS_WINDOW_*`); omit size
    // so this registry isn't mistaken for the runtime source of truth.
    bootstrapMode: 'rpa-auxiliary-window',
  },
  cuaBorder: {
    id: 'cua-border',
    label: 'cua-border',
    route: '/cua-border',
    // Geometry is owned by tauri-plugin-rpa (primary monitor rect); omit size.
    bootstrapMode: 'rpa-auxiliary-window',
  },
  rpaFlowElementHighlight: {
    id: 'rpa-flow-element-highlight',
    label: 'rpa-flow-element-highlight',
    route: '/rpa-flow-element-highlight',
    // Geometry is the selected accessibility element's current bounds.
    bootstrapMode: 'rpa-auxiliary-window',
  },
  recordingToolbar: {
    id: 'recording-toolbar',
    label: 'recording-toolbar',
    route: '/recording-toolbar',
    size: { width: 56, height: 232 },
    bootstrapMode: 'rpa-auxiliary-window',
  },
} as const satisfies Record<string, RpaAuxiliaryWindowSpec>;

export const rpaAuxiliaryWindowSpecs = Object.values(RPA_AUXILIARY_WINDOWS);

const RPA_AUXILIARY_WINDOW_LABELS = new Set<RpaAuxiliaryWindowId>(
  rpaAuxiliaryWindowSpecs.map((spec) => spec.label)
);

export function isRpaAuxiliaryWindowLabel(label: string): label is RpaAuxiliaryWindowId {
  return RPA_AUXILIARY_WINDOW_LABELS.has(label as RpaAuxiliaryWindowId);
}
